import { DateTile, formatEventDateTime, readerTimezone } from '@h2hspark/ui';
import { ArrowRight, Calendar, Check, MapPin, Sparkles, Ticket, Users } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useGoingCounts } from '../attendee-preview/api';
import { eventDetailPath, goingCountLabel } from '../event-discovery/constants';
import { TicketDialog } from '../event-tickets/ticket-dialog';

import {
  EVENT_DETAILS_ACTION,
  matchBandCopy,
  NEXT_UP_PILL,
  SEE_YOUR_MATCHES_ACTION,
  TICKET_ACTION,
  venueAndCity,
  YOURE_GOING_PILL,
  type UpcomingEventRow,
} from './constants';

/**
 * **The Next up card** — `MEM-052` AC-2 and AC-3, `Web App.dc.html` lines 346–368.
 *
 * `--surface-card`, a hairline `--border-subtle`, `--radius-2xl`, `--shadow-md`. The top row at
 * `padding:26px 26px 22px`: the 78px date tile, then the pills (*Next up* in fuchsia-50 /
 * `--brand-accent`, the atmosphere in purple-50 / `--brand-primary`, both 700 uppercase at
 * `var(--text-2xs)`), the name at `var(--text-4xl)` opening the event, the long date at 600
 * `var(--text-md)`, and a meta row — the venue and city beside a `map-pin`, the going count beside
 * `users`. At the right, *You're going* in the success colours. Then the **band**: `--purple-50`
 * with a `--purple-100` rule, a 40px spark circle with `sparkles`, the match line over its
 * sentence, and three actions — *See your matches* filled, *Event details* and *Ticket* outlined.
 *
 * ## What it says about the matches (`MEM-052` AC-3)
 *
 * What is true of this member's own set — never the prototype's fixed *"3 matches ready"*. See
 * `matchBandCopy`.
 *
 * ## What the prototype has here that this does not
 *
 * **"2 seats left of 20"** beside the going count. A member's own payloads carry no seat count,
 * and the event's public page carries no capacity — the count of seats left is the organizer's
 * figure (`MEM-014` AC-3 keeps capacity off every member surface; atmosphere is the size a member
 * sees). The going count stays, under the rule that governs it everywhere: a signed-in member's
 * figure, never a zero.
 *
 * The phone draws this card at full width with its band's actions wrapping beneath the line, the
 * prototype's own `flex-wrap` — no separate phone design.
 */
export function NextUpCard({ event }: { readonly event: UpcomingEventRow }): React.ReactElement {
  const reader = React.useMemo(() => readerTimezone(), []);
  const longDate = formatEventDateTime({
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    timezone: event.timezone,
    reader,
    style: 'long',
  });
  const venue = venueAndCity(event.venueName, event.city);
  const goingCounts = useGoingCounts([event.eventId], { enabled: true });
  const going = goingCountLabel(
    goingCounts.data?.goingCounts.find((count) => count.eventId === event.eventId)?.goingCount,
  );
  const band = matchBandCopy(event.matches);

  return (
    <section
      aria-labelledby="next-up-heading"
      className="overflow-hidden rounded-2xl border border-border-subtle bg-card shadow-md"
    >
      <div className="flex flex-wrap items-start gap-[22px] p-5 md:px-[26px] md:pb-[22px] md:pt-[26px]">
        <DateTile variant="next" startsAt={event.startsAt} timezone={event.timezone} />
        <div className="min-w-0 flex-1 basis-60">
          <div className="mb-2.5 flex flex-wrap gap-2">
            <span className="rounded-pill bg-fuchsia-50 px-[11px] py-[5px] font-brand text-2xs font-bold uppercase tracking-[0.06em] text-brand-accent">
              {NEXT_UP_PILL}
            </span>
            {event.atmosphere === null ? null : (
              <span className="rounded-pill bg-brand-50 px-[11px] py-[5px] font-brand text-2xs font-bold uppercase tracking-[0.06em] text-primary">
                {event.atmosphere}
              </span>
            )}
          </div>
          <h2
            id="next-up-heading"
            className="mb-1.5 text-2xl leading-[1.05] tracking-tight md:text-4xl"
          >
            <Link to={eventDetailPath(event.eventId)} className="hover:text-primary">
              {event.name}
            </Link>
          </h2>
          <div className="font-brand text-md font-semibold text-text-body">{longDate}</div>
          {venue === null && going === null ? null : (
            <div className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm text-muted-foreground">
              {venue === null ? null : (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {venue}
                </span>
              )}
              {going === null ? null : (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {going}
                </span>
              )}
            </div>
          )}
        </div>
        <span className="inline-flex flex-none items-center gap-[7px] self-start rounded-pill bg-success-subtle px-[13px] py-[7px] font-brand text-2xs font-bold uppercase tracking-[0.04em] text-success md:ml-4">
          <Check className="h-[13px] w-[13px]" aria-hidden />
          {YOURE_GOING_PILL}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3.5 border-t border-brand-100 bg-brand-50 px-5 py-[18px] md:px-[26px]">
        <span
          className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-pill bg-spark text-white"
          aria-hidden
        >
          <Sparkles className="h-[19px] w-[19px]" />
        </span>
        <span className="min-w-[180px] flex-1">
          <span className="block font-brand text-md font-bold text-foreground">{band.title}</span>
          <span className="block text-sm text-muted-foreground">{band.description}</span>
        </span>
        {/* The prototype's `flex:none; flex-wrap:wrap`. `flex:none` on the phone would hold the
            three actions on one line wider than the screen, so there the group takes the band's
            full width and wraps inside it; from `md` it sits at the line's right as drawn. */}
        <span className="flex w-full flex-wrap gap-2.5 md:w-auto md:flex-none">
          <Link
            to={`${eventDetailPath(event.eventId)}/matches`}
            className="inline-flex items-center gap-[7px] rounded-pill bg-primary px-5 py-[11px] font-brand text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {SEE_YOUR_MATCHES_ACTION}
            <ArrowRight className="h-[15px] w-[15px]" aria-hidden />
          </Link>
          <Link to={eventDetailPath(event.eventId)} className={OUTLINE_ACTION_CLASS}>
            <Calendar className="h-[15px] w-[15px]" aria-hidden />
            {EVENT_DETAILS_ACTION}
          </Link>
          {/* The ticket opens as the prototype's overlay here too, so a member gets the same
              thing whichever screen they open it from. `/app/events/:id/ticket` still resolves. */}
          <TicketDialog eventId={event.eventId}>
            <button type="button" className={OUTLINE_ACTION_CLASS}>
              <Ticket className="h-[15px] w-[15px]" aria-hidden />
              {TICKET_ACTION}
            </button>
          </TicketDialog>
        </span>
      </div>
    </section>
  );
}

/** The prototype's secondary outline button: `1.5px solid var(--border-default)`, `--text-body`. */
const OUTLINE_ACTION_CLASS =
  'inline-flex items-center gap-[7px] rounded-pill border-[1.5px] border-border bg-card px-[18px] py-[11px] font-brand text-sm font-bold text-text-body transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
